import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { detectPerfTier, PERF_BUDGET, type PerfTier } from '../../perf';

const SceneRoot = lazy(() => import('./SceneRoot'));

/** Static gradient backdrop used while loading and on gated devices */
export function SceneFallback() {
  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        background:
          'radial-gradient(ellipse 70% 55% at 78% 30%, var(--scene-glow-a), transparent 65%),' +
          'radial-gradient(ellipse 60% 50% at 15% 80%, var(--scene-glow-b), transparent 60%)',
      }}
    />
  );
}

export function Scene3D() {
  const [tier] = useState<PerfTier>(() =>
    typeof window === 'undefined' ? 'minimal' : detectPerfTier()
  );
  const [enabled, setEnabled] = useState(false);
  const [killed, setKilled] = useState(false);

  const onKill = useCallback(() => setKilled(true), []);

  useEffect(() => {
    if (!PERF_BUDGET[tier].scene || killed) return;

    // Defer three.js until after first paint / intro curtain
    const start = () => setEnabled(true);
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(start, { timeout: 2500 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(start, 600);
    return () => window.clearTimeout(id);
  }, [tier, killed]);

  const showScene = enabled && !killed && PERF_BUDGET[tier].scene;

  return (
    <>
      <SceneFallback />
      {showScene && (
        <Suspense fallback={null}>
          <SceneRoot tier={tier} onKill={onKill} />
        </Suspense>
      )}
    </>
  );
}
